import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const BODY_WIDTH = 12;
const CHART_HEIGHT = 350;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function CandlestickChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const x = d3.scalePoint()
      .domain(data.map((d) => d.date))
      .range([0, innerW])
      .padding(0.5);

    const priceMin = d3.min(data, (d) => d.low);
    const priceMax = d3.max(data, (d) => d.high);
    const pricePadding = (priceMax - priceMin) * 0.1 || 1;

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks();
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat(formatDate))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickFormat((v) => `$${v}`))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Candles
    data.forEach((d) => {
      const cx = x(d.date);
      const isUp = d.close >= d.open;
      const color = isUp ? '#16a34a' : '#dc2626';

      // Wick (thin line from low to high)
      g.append('line')
        .attr('x1', cx)
        .attr('x2', cx)
        .attr('y1', y(d.high))
        .attr('y2', y(d.low))
        .attr('stroke', color)
        .attr('stroke-width', 1.5);

      // Body (rect from open to close)
      const bodyTop = isUp ? y(d.close) : y(d.open);
      const bodyBottom = isUp ? y(d.open) : y(d.close);
      const bodyHeight = Math.max(1, bodyBottom - bodyTop);

      g.append('rect')
        .attr('x', cx - BODY_WIDTH / 2)
        .attr('y', bodyTop)
        .attr('width', BODY_WIDTH)
        .attr('height', bodyHeight)
        .attr('fill', color)
        .attr('rx', 1);

      // Invisible hover target (wider for easier interaction)
      g.append('rect')
        .attr('x', cx - BODY_WIDTH)
        .attr('y', y(d.high))
        .attr('width', BODY_WIDTH * 2)
        .attr('height', Math.max(1, y(d.low) - y(d.high)))
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: d.date,
            open: d.open,
            close: d.close,
            high: d.high,
            low: d.low,
            avgPrice: d.avgPrice,
            addressCount: d.addressCount,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Candlestick Chart</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No candlestick data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Candlestick Chart</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {(() => {
              const date = new Date(tooltip.date);
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            })()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Open</span>
            <span style={{ fontWeight: 600 }}>${tooltip.open.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Close</span>
            <span style={{ fontWeight: 600, color: tooltip.close >= tooltip.open ? '#16a34a' : '#dc2626' }}>
              ${tooltip.close.toFixed(2)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>High</span>
            <span style={{ fontWeight: 600 }}>${tooltip.high.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Low</span>
            <span style={{ fontWeight: 600 }}>${tooltip.low.toFixed(2)}</span>
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Avg</span>
              <span style={{ fontWeight: 600 }}>${tooltip.avgPrice.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Addresses</span>
              <span style={{ fontWeight: 600 }}>{tooltip.addressCount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
